import React from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const PASSPORT_BASE_URL =
  (typeof process !== 'undefined' && process.env && process.env.VITE_PASSPORT_URL) ||
  'https://staging-nexuspassport.empowerednexus.com';

export const Header: React.FC = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header
      style={{
        backgroundColor: 'var(--bg-secondary)',
        borderBottom: '1px solid var(--border-subtle)',
        height: 'var(--header-height)',
        display: 'flex',
        alignItems: 'center',
        padding: '0 var(--space-4)',
        position: 'sticky',
        top: 0,
        zIndex: 50,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          maxWidth: 'var(--container-max-width)',
          margin: '0 auto',
        }}
      >
        <Link
          to="/"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            textDecoration: 'none',
            color: 'var(--text-primary)',
          }}
          aria-label="LearnOS Home"
        >
          <span style={{ fontSize: '1.5rem' }}>🎓</span>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: 'var(--font-weight-bold)', fontSize: 'var(--font-size-lg)', lineHeight: 1.1 }}>
              LearnOS
            </span>
            <span style={{ fontSize: '0.65rem', color: 'var(--accent-primary)', letterSpacing: '0.05em' }}>
              EMPOWERED NEXUS
            </span>
          </div>
        </Link>

        {/* Desktop Navigation Links */}
        <nav
          style={{
            display: 'none',
            alignItems: 'center',
            gap: 'var(--space-2)',
          }}
          className="desktop-nav"
        >
          <NavLink
            to="/"
            end
            className={({ isActive }) => (isActive ? 'btn-secondary' : 'touch-nav-item')}
            style={({ isActive }) => ({
              color: isActive ? 'var(--accent-primary)' : 'var(--text-secondary)',
              textDecoration: 'none',
              borderRadius: 'var(--radius-md)',
            })}
          >
            Home
          </NavLink>
          <NavLink
            to="/library"
            className={({ isActive }) => (isActive ? 'btn-secondary' : 'touch-nav-item')}
            style={({ isActive }) => ({
              color: isActive ? 'var(--accent-primary)' : 'var(--text-secondary)',
              textDecoration: 'none',
              borderRadius: 'var(--radius-md)',
            })}
          >
            Free Library
          </NavLink>
          {isAuthenticated && (
            <>
              <NavLink
                to="/my-learning"
                className={({ isActive }) => (isActive ? 'btn-secondary' : 'touch-nav-item')}
                style={({ isActive }) => ({
                  color: isActive ? 'var(--accent-primary)' : 'var(--text-secondary)',
                  textDecoration: 'none',
                  borderRadius: 'var(--radius-md)',
                })}
              >
                My Learning
              </NavLink>
              <NavLink
                to="/progress"
                className={({ isActive }) => (isActive ? 'btn-secondary' : 'touch-nav-item')}
                style={({ isActive }) => ({
                  color: isActive ? 'var(--accent-primary)' : 'var(--text-secondary)',
                  textDecoration: 'none',
                  borderRadius: 'var(--radius-md)',
                })}
              >
                Progress
              </NavLink>
              {user?.isEducator && (
                <NavLink
                  to="/authoring"
                  className={({ isActive }) => (isActive ? 'btn-secondary' : 'touch-nav-item')}
                  style={({ isActive }) => ({
                    color: isActive ? 'var(--accent-primary)' : 'var(--text-secondary)',
                    textDecoration: 'none',
                    borderRadius: 'var(--radius-md)',
                    fontWeight: 'bold',
                  })}
                >
                  ✏️ Teaching Studio
                </NavLink>
              )}
            </>
          )}
        </nav>

        {/* User Auth Info / Login */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          {isAuthenticated && user ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
              <a
                href={`${PASSPORT_BASE_URL}/app`}
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text-secondary)',
                  textDecoration: 'none',
                  padding: 'var(--space-1) var(--space-2)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-md)',
                  display: 'none',
                }}
                className="user-greeting"
                title="Open NexusPassport Ecosystem Gateway"
              >
                NexusPassport
              </a>
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text-secondary)',
                  display: 'none',
                }}
                className="user-greeting"
              >
                {user.username || user.email}
              </span>
              <button
                onClick={handleLogout}
                className="btn-secondary"
                style={{ fontSize: 'var(--font-size-sm)', padding: 'var(--space-1) var(--space-3)' }}
                aria-label="Log out"
              >
                Log Out
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <Link
                to="/login"
                className="btn-secondary"
                style={{
                  fontSize: 'var(--font-size-sm)',
                  padding: 'var(--space-1) var(--space-3)',
                  textDecoration: 'none',
                }}
                aria-label="Sign In (Log In)"
              >
                Sign In
              </Link>
              <a
                href={`${PASSPORT_BASE_URL}/signup?target_app=learnos`}
                className="btn-primary"
                style={{
                  fontSize: 'var(--font-size-sm)',
                  padding: 'var(--space-1) var(--space-3)',
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                Create Free Account
              </a>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @media (min-width: 768px) {
          .desktop-nav {
            display: flex !important;
          }
          .user-greeting {
            display: inline-block !important;
          }
        }
      `}</style>
    </header>
  );
};
